import React, { useEffect } from "react";
import es from "date-fns/locale/es";
import DatePicker, { registerLocale } from "react-datepicker";
import Selector from "canvas-core-react/lib/Selector";
import TextField from "canvas-core-react/lib/TextField";
import { useTranslation } from "react-i18next";
import { capitalize, formatDashDate } from "../../utils/functions";

const currentDate = new Date();
const initialDate = new Date(new Date().setMonth(currentDate.getMonth() - 1));

const FiltersByReport = (props) => {
  const { currencies, filterValue, setFilterValue, userType } = props;
  registerLocale("es", es);
  const { t } = useTranslation();
  // const [filterValue, setFilterValue] = useState({});

  useEffect(() => {
    if (props.reportType === "BILLS") {
      // Colocamos filtros por defecto para fechas
      setDefaultDates();
    } else {
      setFilterValue({});
    }
  }, [props.reportType]);

  const isClient = userType === "CSA";

  // Actualizamos el valor de los inputs
  const onChange = (e) => {
    var currentValue = { ...filterValue };

    if (e.target.value === "") {
      delete currentValue[e.target.name];
    } else {
      currentValue[e.target.name] = {
        value: e.target.value,
        operator: e.target.dataset.operator || "eq",
        property: e.target.dataset.id,
      };
    }

    setFilterValue(currentValue);
    updateFilters(currentValue);
  };

  // Actualizamos los filtros que vamos a utilizar para la búsqueda de reportes
  const updateFilters = (values) => {
    var filters = [];

    Object.keys(values).map((key) => {
      var input = values[key];
      var value = input.value;

      if (input.value instanceof Date) {
        var date = new Date(input.value);
        if (key == "toDate") {
          date.setDate(date.getDate() + 1);
        }
        value = formatDashDate(date);
      }

      filters.push({
        property: input.property,
        operator: input.operator,
        value,
      });
      return true;
    });

    props.updateFilters(filters);
  };

  const setDefaultDates = () => {
    const currentValue = {
      fromDate: {
        value: isClient ? initialDate : currentDate,
        operator: "ge",
        property: "createDate",
      },
      toDate: {
        value: currentDate,
        operator: "le",
        property: "createDate",
      },
    };

    setFilterValue(currentValue);
    updateFilters(currentValue);
  };

  const { labels, placeholders, states } = {
    labels: t("general.labels", { returnObjects: true }),
    placeholders: t("general.placeholders", { returnObjects: true }),
    states: t("general.billStates", { returnObjects: true }),
  };

  const print = (filter) => {
    switch (filter.type) {
      case "date": {
        const { double, key, name } = filter;

        let dateFilters = double
          ? [
              {
                key: "fromDate",
                name: `${t("report.options.from")}`,
                operator: "ge",
                parameter: key,
                minDate: isClient ? new Date("2024/01/01") : undefined,
                maxDate: filterValue["toDate"]?.value,
              },
              {
                key: "toDate",
                name: `${t("report.options.to")}`,
                operator: "le",
                parameter: key,
                minDate: filterValue["fromDate"]?.value,
                maxDate: currentDate,
              },
            ]
          : [
              {
                key,
                operator: "eq",
                placeholderText: placeholders.date,
                parameter: key,
                defaultValue: null,
              },
            ];

        return (
          <div
            key={key}
            className={`row col-lg-${double ? "12" : "6"} flex-column pt-3`}
          >
            <label>{name}</label>
            <div className="row">
              {dateFilters.map(
                ({
                  key,
                  name,
                  defaultValue,
                  operator,
                  parameter,
                  ...restProps
                }) => {
                  return (
                    <div
                      key={key}
                      className={`input col-lg-${double ? "6" : "12"}`}
                    >
                      <label>{name}</label>
                      <DatePicker
                        id={key}
                        name={key}
                        selected={filterValue[key]?.value || defaultValue}
                        onChange={(date) =>
                          onChange({
                            target: {
                              name: key,
                              value: date,
                              dataset: {
                                operator: operator,
                                id: parameter,
                              },
                            },
                          })
                        }
                        showMonthDropdown
                        showYearDropdown
                        showPopperArrow={false}
                        dropdownMode="scroll"
                        locale="es"
                        autoComplete={`off`}
                        dateFormat="dd/MM/yyyy"
                        {...restProps}
                      />
                    </div>
                  );
                }
              )}
            </div>
          </div>
        );
      }

      case "selector":
        return (
          <div key={`${filter.key}`} className="col-lg-6 px-4 pt-3">
            <label htmlFor={filter.key}>{filter.name}</label>
            <Selector
              id={`${filter.key}`}
              label=""
              className="no-label"
              name={`${filter.key}`}
              onChange={(e) =>
                onChange({
                  target: {
                    value: e.target.value,
                    name: e.target.name,
                    dataset: {
                      id: filter.parameter,
                      operator: filter.operator,
                    },
                  },
                })
              }
              placeholder={filter.placeholder}
              value={filterValue[filter.key]?.value || ""}
            >
              {filter.options.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Selector>
          </div>
        );
      default: {
        const onKeyPress = (event) => {
          if (!/[0-9]/.test(event.key) && filter.onlyNumber) {
            event.preventDefault();
          }
        };

        return (
          <div key={`${filter.key}`} className="col-lg-6 pt-3">
            <TextField
              id={`${filter.key}`}
              label={filter.name}
              name={filter.key}
              onKeyPress={onKeyPress}
              onChange={(e) =>
                onChange({
                  target: {
                    value: e.target.value,
                    name: e.target.name,
                    dataset: {
                      id: filter.parameter,
                      operator: filter.operator,
                    },
                  },
                })
              }
              value={
                filterValue[filter.key] !== undefined
                  ? filterValue[filter.key].value
                  : ""
              }
              placeholder={`${filter.placeholder}`}
            />
          </div>
        );
      }
    }
  };

  const getFilter = (type) => {
    var filters = [];
    const fixedCurrencies = currencies.map(
      ({ currency, currencyDescription }) => ({
        value: currency,
        label: capitalize(currencyDescription),
      })
    );

    const statesOptions = statesAvailables.map((item) => ({
      label: states[item],
      value: item,
    }));

    switch (type) {
      case "CUSTOMERS":
        filters.push({
          key: "cif",
          name: "CIF",
          parameter: "institution.cif",
          placeholder: `${t("report.options.placeholder_cif")}`,
        });
        break;
      case "PROVIDERS":
        if (isClient) {
          filters.push({
            key: "status",
            name: t("report.options.provider_status"),
            parameter: "provider.status",
            placeholder: t("report.options.provider_status"),
            type: "selector",
            operator: "eq",
            options: status.map((item) => ({
              value: item,
              label: t(`general.states.${item}`),
            })),
          });
        } else {
          filters.push(
            {
              key: "cif",
              name: "CIF",
              parameter: "company.institution.cif",
              placeholder: `${t("report.options.placeholder_cif")}`,
            },
            {
              key: "types",
              name: t("report.options.provider_type"),
              parameter: "toBillDiscount",
              placeholder: t("report.options.provider_type"),
              type: "selector",
              operator: "eq",
              options: typeProvider.map(({ text, value }) => ({
                value: value,
                label: t(`providerType.${text}`),
              })),
            },
            {
              key: "businessId",
              name: t("report.options.provider_id"),
              parameter: "provider.institution.businessId",
              placeholder: `${t("report.options.provider_id")}`,
            },
            {
              key: "companyName",
              name: t("report.options.provider_name"),
              parameter: "provider.institution.companyName",
              placeholder: `${t("report.options.provider_name")}`,
            },
            {
              key: "status",
              name: t("report.options.provider_status"),
              parameter: "provider.status",
              placeholder: t("report.options.provider_status"),
              type: "selector",
              operator: "eq",
              options: status.map((item) => ({
                value: item,
                label: t(`general.states.${item}`),
              })),
            },
            {
              key: "invitationStatus",
              name: t("report.options.invitation_status"),
              parameter: "invitationStatus",
              placeholder: t("report.options.invitation_status"),
              type: "selector",
              operator: "eq",
              options: invitationStatus.map((item) => ({
                value: item,
                label: t(`general.inviteStates.${item}`),
              })),
            }
          );
        }

        break;

      case "BILLS":
        if (isClient) {
          filters.push({
            key: "createDate",
            type: "date",
            name: labels.createDate,
            double: true,
          });
        } else {
          filters.push(
            {
              key: "cif",
              name: "CIF",
              parameter: "company.institution.cif",
              placeholder: `${t("report.options.placeholder_cif")}`,
            },
            {
              key: "createDate",
              type: "date",
              name: labels.createDate,
              double: true,
            },
            { key: "dateOfIssue", type: "date", name: labels.dateOfIssue },
            {
              key: "expirationDate",
              type: "date",
              name: labels.expirationDate,
            },
            {
              key: "totalAmount",
              name: labels.totalAmount,
              parameter: "totalAmount",
              placeholder: placeholders.amount,
              operator: "eq",
              onlyNumber: true,
            },
            {
              key: "status",
              name: labels.state,
              parameter: "status",
              placeholder: labels.state,
              operator: "eq",
              type: "selector",
              options: statesOptions,
            },
            {
              key: "currency",
              name: `${t("report.options.currency")}`,
              parameter: "currency",
              placeholder: `${t("report.options.placeholder_currency")}`,
              type: "selector",
              operator: "eq",
              options: fixedCurrencies,
            }
          );
        }

        break;
    }

    return filters.map((filter) => print(filter));
  };

  return getFilter(props.reportType);
};

export default FiltersByReport;

const typeProvider = [
  { text: "discount", value: true },
  { text: "directPayment", value: false },
];

const status = ["ACTIVE", "INACTIVE", "DELETED"];

const statesAvailables = [
  "DRAFT",
  "PENDING",
  "ACCEPTED_PARTIALLY",
  "CLIENT_ACCEPTED",
  "PROVIDER_ACCEPTED",
  "ACCEPTED",
  "APPROVED_PARTIALLY",
  "APPROVED",
  "REJECTED",
  "DELETED",
  "DELETE_PENDING",
  "VALIDATED",
  "PROCESSED",
  "PAID",
  "PAID_REJECTED",
  "DDAY",
  "CLIENT_CANCELLED",
  "PROVIDER_CANCELLED",
  "CANCELLED",
  "ERROR",
  "DISCOUNT",
  "REJECTED_DELETE",
  "BLOCKED",
  "APPROVAL_PENDING",
  "EXPIRED",
  "TRANSITORY",
  "ACTIVE",
  "INACTIVE",
  "PENDING_CHANGES",
  "SUCCESS",
  "MODIFY_ACCOUNT",
  "ADDITION",
  "MODIFICATION",
  "CREATION",
  "CLIENT_DELETE",
];

const invitationStatus = [
  "PENDING",
  "PENDING_APPROVAL",
  "NOTIFIED",
  "INVITED",
  "PROCESSED",
  "ACCEPTED",
  "REJECTED",
  "PROCESSED_INVITATION",
  "VERIFIED",
  "ACTIVE",
  "INACTIVE",
  "DELETED",
  "PENDING_APPROVAL_INVITATION",
];
